/**
 * Product Image Slider Component
 * Handles main image display with thumbnail carousel navigation
 * Supports images, videos, and external videos
 * Includes fullscreen zoom overlay with pinch-to-zoom, scroll zoom, and pan
 */

if (!customElements.get('product-image-slider')) {
  class ProductImageSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.mediaCount = 0;
      this.thumbnailScrollAmount = 120;
      this.mediaData = {};
      this._imageCache = {};
      this._loadingTimer = null;

      // Zoom state
      this._zoomOverlay = null;
      this._zoomImage = null;
      this._zoomScale = 1;
      this._zoomMinScale = 1;
      this._zoomMaxScale = 4;
      this._zoomPanX = 0;
      this._zoomPanY = 0;
      this._zoomIsDragging = false;
      this._zoomDragStartX = 0;
      this._zoomDragStartY = 0;
      this._zoomDragStartPanX = 0;
      this._zoomDragStartPanY = 0;
      this._zoomLevelTimeout = null;
      this._zoomHintShown = false;

      // Pinch state
      this._pinchStartDist = 0;
      this._pinchStartScale = 1;
      this._pinchMidX = 0;
      this._pinchMidY = 0;
      this._lastTapTime = 0;
    }

    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
    }

    init() {
      this.mainImage = this.querySelector('.product-image-slider__main-image');
      this.mainMediaWrapper = this.querySelector('.product-image-slider__main-media');
      this.thumbnails = this.querySelectorAll('.product-image-slider__thumbnail');
      this.thumbnailsContainer = this.querySelector('.product-image-slider__thumbnails');
      this.prevMainBtn = this.querySelector('.product-image-slider__main-nav--prev');
      this.nextMainBtn = this.querySelector('.product-image-slider__main-nav--next');
      this.prevThumbBtn = this.querySelector('.product-image-slider__thumb-nav--prev');
      this.nextThumbBtn = this.querySelector('.product-image-slider__thumb-nav--next');

      this.loadMediaData();

      this.mediaCount = this.thumbnails.length;

      // Build the list of image-only indices for zoom navigation
      this._imageIndices = [];
      this.thumbnails.forEach((thumb, i) => {
        const type = thumb.dataset.mediaType || 'image';
        if (type === 'image') this._imageIndices.push(i);
      });

      if (this.mediaCount <= 1) {
        this.classList.add('product-image-slider--single');
      }

      if (this.mediaCount > 1) {
        this.bindEvents();
        this.updateNavButtons();
        this.updateThumbnailNavButtons();
      }

      // Bind zoom events (works even for single image)
      this.bindZoomEvents();

      // Preload all gallery images so every swap is instant
      this.preloadAllImages();
    }

    loadMediaData() {
      const scriptTag = this.querySelector('script[type="application/json"][id^="ProductMediaData-"]');
      if (scriptTag) {
        try {
          this.mediaData = JSON.parse(scriptTag.textContent);
        } catch (e) {
          console.warn('Failed to parse product media data:', e);
          this.mediaData = {};
        }
      }
    }

    bindEvents() {
      this.thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goToSlide(index);
        });
      });

      if (this.prevMainBtn) {
        this.prevMainBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.prevSlide();
        });
      }
      if (this.nextMainBtn) {
        this.nextMainBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.nextSlide();
        });
      }

      if (this.prevThumbBtn) {
        this.prevThumbBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.scrollThumbnails('prev');
        });
      }
      if (this.nextThumbBtn) {
        this.nextThumbBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.scrollThumbnails('next');
        });
      }

      if (this.thumbnailsContainer) {
        this.thumbnailsContainer.addEventListener('scroll', () => {
          this.updateThumbnailNavButtons();
        });
      }

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          this.prevSlide();
        } else if (e.key === 'ArrowRight') {
          this.nextSlide();
        }
      });

      this.bindTouchEvents();
    }

    /* ===========================
       Zoom Events
       =========================== */
    bindZoomEvents() {
      // Zoom icon click
      const zoomIcon = this.querySelector('.product-image-slider__zoom-icon');
      if (zoomIcon) {
        zoomIcon.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openZoom();
        });
      }

      // Click on main image to open zoom
      if (this.mainMediaWrapper) {
        this.mainMediaWrapper.addEventListener('click', (e) => {
          // Don't open zoom if clicking on zoom icon (handled above),
          // nav buttons, or video elements
          if (e.target.closest('.product-image-slider__zoom-icon') ||
              e.target.closest('.product-image-slider__main-nav') ||
              e.target.closest('.product-image-slider__main-video') ||
              e.target.closest('video') ||
              e.target.closest('iframe')) {
            return;
          }

          const currentThumb = this.thumbnails[this.currentIndex];
          const mediaType = currentThumb ? (currentThumb.dataset.mediaType || 'image') : 'image';
          if (mediaType === 'image') {
            this.openZoom();
          }
        });
      }
    }

    /**
     * Get the current image src from the main display
     */
    _getCurrentImageSrc() {
      if (this.mainImage && this.mainImage.src) {
        // Use the highest resolution available
        return this.mainImage.src;
      }
      const thumb = this.thumbnails[this.currentIndex];
      return thumb ? thumb.dataset.fullSrc : null;
    }

    /**
     * Open fullscreen zoom overlay
     */
    openZoom() {
      const src = this._getCurrentImageSrc();
      if (!src) return;

      // Create overlay if it doesn't exist
      if (!this._zoomOverlay) {
        this._createZoomOverlay();
      }

      // Set image source
      this._zoomImage.src = src;
      this._zoomImage.alt = this.mainImage ? this.mainImage.alt : '';

      // Reset zoom state
      this._zoomScale = 1;
      this._zoomPanX = 0;
      this._zoomPanY = 0;
      this._applyZoomTransform(false);

      // Update counter & nav
      this._updateZoomNav();

      // Show overlay
      this._zoomOverlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';

      // Show hint on first use
      if (!this._zoomHintShown) {
        this._zoomHintShown = true;
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        const hintEl = this._zoomOverlay.querySelector('.product-zoom-overlay__hint');
        if (hintEl) {
          hintEl.textContent = isTouchDevice
            ? 'Pinch to zoom • Drag to pan'
            : 'Scroll to zoom • Drag to pan';
        }
      }

      // Focus the overlay for keyboard events
      this._zoomOverlay.focus();
    }

    /**
     * Close zoom overlay
     */
    closeZoom() {
      if (!this._zoomOverlay) return;
      this._zoomOverlay.classList.remove('is-active');
      document.body.style.overflow = '';
    }

    /**
     * Create the zoom overlay DOM
     */
    _createZoomOverlay() {
      const overlay = document.createElement('div');
      overlay.className = 'product-zoom-overlay';
      overlay.setAttribute('tabindex', '-1');
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-label', 'Image zoom');

      overlay.innerHTML = `
        <button class="product-zoom-overlay__close" aria-label="Close zoom">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <button class="product-zoom-overlay__nav product-zoom-overlay__nav--prev" aria-label="Previous image">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <button class="product-zoom-overlay__nav product-zoom-overlay__nav--next" aria-label="Next image">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 6 15 12 9 18"></polyline>
          </svg>
        </button>
        <img class="product-zoom-overlay__image" src="" alt="" draggable="false">
        <div class="product-zoom-overlay__counter"></div>
        <div class="product-zoom-overlay__zoom-level"></div>
        <div class="product-zoom-overlay__hint"></div>
      `;

      document.body.appendChild(overlay);
      this._zoomOverlay = overlay;
      this._zoomImage = overlay.querySelector('.product-zoom-overlay__image');
      this._zoomCounter = overlay.querySelector('.product-zoom-overlay__counter');
      this._zoomLevelEl = overlay.querySelector('.product-zoom-overlay__zoom-level');

      // Bind close
      overlay.querySelector('.product-zoom-overlay__close').addEventListener('click', () => this.closeZoom());

      // Close on background click (only if not dragging and at 1x zoom)
      let mouseDownTarget = null;
      overlay.addEventListener('mousedown', (e) => { mouseDownTarget = e.target; });
      overlay.addEventListener('mouseup', (e) => {
        if (mouseDownTarget === overlay && e.target === overlay && !this._zoomIsDragging && this._zoomScale <= 1) {
          this.closeZoom();
        }
      });

      // Prev/next in zoom
      overlay.querySelector('.product-zoom-overlay__nav--prev').addEventListener('click', (e) => {
        e.stopPropagation();
        this._zoomPrev();
      });
      overlay.querySelector('.product-zoom-overlay__nav--next').addEventListener('click', (e) => {
        e.stopPropagation();
        this._zoomNext();
      });

      // Keyboard
      overlay.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeZoom();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this._zoomPrev();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this._zoomNext();
        } else if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          this._zoomTo(this._zoomScale * 1.3);
        } else if (e.key === '-') {
          e.preventDefault();
          this._zoomTo(this._zoomScale / 1.3);
        } else if (e.key === '0') {
          e.preventDefault();
          this._resetZoom();
        }
      });

      // Mouse wheel zoom
      overlay.addEventListener('wheel', (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const rect = overlay.getBoundingClientRect();
        const mouseX = e.clientX - rect.left - rect.width / 2;
        const mouseY = e.clientY - rect.top - rect.height / 2;
        this._zoomAtPoint(this._zoomScale * delta, mouseX, mouseY);
      }, { passive: false });

      // Mouse drag for panning
      overlay.addEventListener('mousedown', (e) => {
        if (e.target.closest('button')) return;
        this._zoomIsDragging = false;
        this._zoomDragStartX = e.clientX;
        this._zoomDragStartY = e.clientY;
        this._zoomDragStartPanX = this._zoomPanX;
        this._zoomDragStartPanY = this._zoomPanY;
        overlay.classList.add('is-grabbing');

        const onMouseMove = (ev) => {
          const dx = ev.clientX - this._zoomDragStartX;
          const dy = ev.clientY - this._zoomDragStartY;
          if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
            this._zoomIsDragging = true;
          }
          this._zoomPanX = this._zoomDragStartPanX + dx;
          this._zoomPanY = this._zoomDragStartPanY + dy;
          this._applyZoomTransform(true);
        };

        const onMouseUp = () => {
          overlay.classList.remove('is-grabbing');
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
          this._constrainPan();
          // Reset drag flag after a small delay
          setTimeout(() => { this._zoomIsDragging = false; }, 50);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      });

      // Touch events for pinch-to-zoom and panning
      this._bindZoomTouchEvents(overlay);
    }

    /**
     * Bind touch events on the zoom overlay for pinch & pan
     */
    _bindZoomTouchEvents(overlay) {
      let activeTouches = [];
      let singleTouchStart = null;
      let singleTouchMoved = false;
      let isSwipeGesture = false; // Track whether this gesture is a horizontal swipe (at 1x zoom)

      overlay.addEventListener('touchstart', (e) => {
        if (e.target.closest('button')) return;
        activeTouches = Array.from(e.touches);
        isSwipeGesture = false;

        if (e.touches.length === 2) {
          // Start pinch
          const t1 = e.touches[0];
          const t2 = e.touches[1];
          this._pinchStartDist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
          this._pinchStartScale = this._zoomScale;

          const rect = overlay.getBoundingClientRect();
          this._pinchMidX = ((t1.clientX + t2.clientX) / 2) - rect.left - rect.width / 2;
          this._pinchMidY = ((t1.clientY + t2.clientY) / 2) - rect.top - rect.height / 2;
        } else if (e.touches.length === 1) {
          // Start drag / potential double-tap / potential swipe
          singleTouchStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
          singleTouchMoved = false;
          this._zoomDragStartX = e.touches[0].clientX;
          this._zoomDragStartY = e.touches[0].clientY;
          this._zoomDragStartPanX = this._zoomPanX;
          this._zoomDragStartPanY = this._zoomPanY;
        }
      }, { passive: true });

      overlay.addEventListener('touchmove', (e) => {
        if (e.target.closest('button')) return;
        e.preventDefault();

        if (e.touches.length === 2) {
          // Pinch zoom
          const t1 = e.touches[0];
          const t2 = e.touches[1];
          const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
          const newScale = this._pinchStartScale * (dist / this._pinchStartDist);
          this._zoomAtPoint(newScale, this._pinchMidX, this._pinchMidY, true);
        } else if (e.touches.length === 1) {
          const dx = e.touches[0].clientX - this._zoomDragStartX;
          const dy = e.touches[0].clientY - this._zoomDragStartY;
          if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
            singleTouchMoved = true;
          }

          // When not zoomed in, treat horizontal drags as swipe gestures
          // instead of panning — this lets users swipe to navigate images
          if (this._zoomScale <= 1 && Math.abs(dx) > Math.abs(dy)) {
            isSwipeGesture = true;
          }

          if (!isSwipeGesture) {
            // Zoomed-in pan behaviour (existing)
            this._zoomPanX = this._zoomDragStartPanX + dx;
            this._zoomPanY = this._zoomDragStartPanY + dy;
            this._applyZoomTransform(true);
          }
        }
      }, { passive: false });

      overlay.addEventListener('touchend', (e) => {
        if (e.target.closest('button')) return;

        if (e.touches.length === 0) {
          // Handle swipe navigation when not zoomed in
          if (isSwipeGesture && singleTouchStart) {
            const lastTouch = e.changedTouches[0];
            const swipeDX = lastTouch.clientX - singleTouchStart.x;
            const swipeDY = lastTouch.clientY - singleTouchStart.y;
            const swipeThreshold = 50;

            if (Math.abs(swipeDX) > swipeThreshold && Math.abs(swipeDX) > Math.abs(swipeDY)) {
              if (swipeDX < 0) {
                this._zoomNext();
              } else {
                this._zoomPrev();
              }
              // Skip double-tap detection after a swipe
              singleTouchStart = null;
              singleTouchMoved = false;
              isSwipeGesture = false;
              return;
            }
          }

          isSwipeGesture = false;
          this._constrainPan();

          // Double-tap detection
          if (!singleTouchMoved && singleTouchStart) {
            const now = Date.now();
            if (now - this._lastTapTime < 300) {
              // Double-tap: toggle between 1x and 2x
              if (this._zoomScale > 1.1) {
                this._resetZoom();
              } else {
                const rect = overlay.getBoundingClientRect();
                const tapX = singleTouchStart.x - rect.left - rect.width / 2;
                const tapY = singleTouchStart.y - rect.top - rect.height / 2;
                this._zoomAtPoint(2.5, tapX, tapY);
              }
              this._lastTapTime = 0;
            } else {
              this._lastTapTime = now;
            }
          }

          singleTouchStart = null;
          singleTouchMoved = false;
        }
      }, { passive: true });
    }

    /**
     * Apply the current zoom transform to the image
     */
    _applyZoomTransform(noTransition) {
      if (!this._zoomImage) return;
      if (noTransition) {
        this._zoomImage.classList.add('no-transition');
      } else {
        this._zoomImage.classList.remove('no-transition');
      }
      this._zoomImage.style.transform = `translate(${this._zoomPanX}px, ${this._zoomPanY}px) scale(${this._zoomScale})`;
    }

    /**
     * Zoom to a specific scale, centered on a point
     */
    _zoomAtPoint(newScale, pointX, pointY, noTransition) {
      newScale = Math.max(this._zoomMinScale, Math.min(this._zoomMaxScale, newScale));

      // Adjust pan to zoom towards the pointer
      const scaleRatio = newScale / this._zoomScale;
      this._zoomPanX = pointX - scaleRatio * (pointX - this._zoomPanX);
      this._zoomPanY = pointY - scaleRatio * (pointY - this._zoomPanY);
      this._zoomScale = newScale;

      this._applyZoomTransform(!!noTransition);
      this._showZoomLevel();
    }

    /**
     * Zoom to a specific scale (centered)
     */
    _zoomTo(newScale) {
      newScale = Math.max(this._zoomMinScale, Math.min(this._zoomMaxScale, newScale));
      this._zoomScale = newScale;
      this._constrainPan();
      this._applyZoomTransform(false);
      this._showZoomLevel();
    }

    /**
     * Reset zoom to fit
     */
    _resetZoom() {
      this._zoomScale = 1;
      this._zoomPanX = 0;
      this._zoomPanY = 0;
      this._applyZoomTransform(false);
      this._showZoomLevel();
    }

    /**
     * Constrain pan so image doesn't go too far offscreen
     */
    _constrainPan() {
      if (!this._zoomOverlay || !this._zoomImage) return;

      // Only constrain if zoomed in
      if (this._zoomScale <= 1) {
        this._zoomPanX = 0;
        this._zoomPanY = 0;
        this._applyZoomTransform(false);
        return;
      }

      const overlayRect = this._zoomOverlay.getBoundingClientRect();
      const imgW = this._zoomImage.naturalWidth || this._zoomImage.offsetWidth;
      const imgH = this._zoomImage.naturalHeight || this._zoomImage.offsetHeight;

      // Compute displayed size: image is sized to fit viewport at scale=1
      const fitScale = Math.min(overlayRect.width / imgW, overlayRect.height / imgH, 1);
      const displayW = imgW * fitScale * this._zoomScale;
      const displayH = imgH * fitScale * this._zoomScale;

      const maxPanX = Math.max(0, (displayW - overlayRect.width) / 2);
      const maxPanY = Math.max(0, (displayH - overlayRect.height) / 2);

      this._zoomPanX = Math.max(-maxPanX, Math.min(maxPanX, this._zoomPanX));
      this._zoomPanY = Math.max(-maxPanY, Math.min(maxPanY, this._zoomPanY));

      this._applyZoomTransform(false);
    }

    /**
     * Show current zoom level indicator briefly
     */
    _showZoomLevel() {
      if (!this._zoomLevelEl) return;
      const percent = Math.round(this._zoomScale * 100);
      this._zoomLevelEl.textContent = `${percent}%`;
      this._zoomLevelEl.classList.add('is-visible');

      if (this._zoomLevelTimeout) clearTimeout(this._zoomLevelTimeout);
      this._zoomLevelTimeout = setTimeout(() => {
        this._zoomLevelEl.classList.remove('is-visible');
      }, 1200);
    }

    /**
     * Navigate to previous image in zoom overlay
     */
    _zoomPrev() {
      const curImgIdx = this._imageIndices.indexOf(this.currentIndex);
      if (curImgIdx > 0) {
        const prevIdx = this._imageIndices[curImgIdx - 1];
        this.goToSlide(prevIdx);
        this._loadZoomImage(prevIdx);
      }
    }

    /**
     * Navigate to next image in zoom overlay
     */
    _zoomNext() {
      const curImgIdx = this._imageIndices.indexOf(this.currentIndex);
      if (curImgIdx < this._imageIndices.length - 1) {
        const nextIdx = this._imageIndices[curImgIdx + 1];
        this.goToSlide(nextIdx);
        this._loadZoomImage(nextIdx);
      }
    }

    /**
     * Load a specific image into the zoom overlay
     */
    _loadZoomImage(index) {
      const thumb = this.thumbnails[index];
      if (!thumb || !this._zoomImage) return;

      const src = thumb.dataset.fullSrc;
      if (!src) return;

      this._zoomImage.src = src;
      this._zoomImage.alt = thumb.dataset.alt || '';

      // Reset zoom when changing images
      this._resetZoom();
      this._updateZoomNav();
    }

    /**
     * Update zoom overlay navigation (counter, disabled states)
     */
    _updateZoomNav() {
      if (!this._zoomOverlay) return;

      const imgCount = this._imageIndices.length;
      const curImgIdx = this._imageIndices.indexOf(this.currentIndex);

      // Counter
      if (this._zoomCounter) {
        if (imgCount > 1) {
          this._zoomCounter.textContent = `${curImgIdx + 1} / ${imgCount}`;
          this._zoomCounter.style.display = '';
        } else {
          this._zoomCounter.style.display = 'none';
        }
      }

      // Nav buttons
      const prevBtn = this._zoomOverlay.querySelector('.product-zoom-overlay__nav--prev');
      const nextBtn = this._zoomOverlay.querySelector('.product-zoom-overlay__nav--next');

      if (imgCount <= 1) {
        if (prevBtn) prevBtn.style.display = 'none';
        if (nextBtn) nextBtn.style.display = 'none';
      } else {
        if (prevBtn) {
          prevBtn.style.display = '';
          prevBtn.disabled = curImgIdx <= 0;
        }
        if (nextBtn) {
          nextBtn.style.display = '';
          nextBtn.disabled = curImgIdx >= imgCount - 1;
        }
      }
    }

    /* ===========================
       Touch Events (gallery swipe)
       =========================== */
    bindTouchEvents() {
      if (!this.mainMediaWrapper) return;

      let touchStartX = 0;
      let touchStartY = 0;
      let touchEndX = 0;
      let touchEndY = 0;
      let isSwiping = false;

      this.mainMediaWrapper.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
        touchStartY = e.changedTouches[0].screenY;
        isSwiping = true;
      }, { passive: true });

      this.mainMediaWrapper.addEventListener('touchmove', (e) => {
        if (!isSwiping) return;

        touchEndX = e.changedTouches[0].screenX;
        touchEndY = e.changedTouches[0].screenY;

        const diffX = Math.abs(touchStartX - touchEndX);
        const diffY = Math.abs(touchStartY - touchEndY);

        if (diffX > diffY && diffX > 10) {
          e.preventDefault();
        }
      }, { passive: false });

      this.mainMediaWrapper.addEventListener('touchend', (e) => {
        if (!isSwiping) return;
        isSwiping = false;

        touchEndX = e.changedTouches[0].screenX;
        touchEndY = e.changedTouches[0].screenY;

        const diffX = touchStartX - touchEndX;
        const diffY = Math.abs(touchStartY - touchEndY);

        if (Math.abs(diffX) > 50 && Math.abs(diffX) > diffY) {
          if (diffX > 0) {
            this.nextSlide();
          } else {
            this.prevSlide();
          }
        }

        touchStartX = 0;
        touchStartY = 0;
        touchEndX = 0;
        touchEndY = 0;
      }, { passive: true });
    }

    /**
     * Pause any currently playing video in the main display
     */
    pauseCurrentVideo() {
      if (!this.mainMediaWrapper) return;

      const video = this.mainMediaWrapper.querySelector('video');
      if (video) {
        video.pause();
      }

      const iframe = this.mainMediaWrapper.querySelector('iframe');
      if (iframe) {
        iframe.src = '';
      }
    }

    /**
     * Render the appropriate media (image or video) in the main display
     */
    renderMedia(mediaId, thumbnail) {
      if (!this.mainMediaWrapper) return;

      const mediaType = thumbnail.dataset.mediaType || 'image';
      const mediaInfo = this.mediaData[mediaId];
      const newSrc = thumbnail.dataset.fullSrc;
      const newAlt = thumbnail.dataset.alt || '';
      const zoomIcon = this.mainMediaWrapper.querySelector('.product-image-slider__zoom-icon');

      this.pauseCurrentVideo();

      const existingVideo = this.mainMediaWrapper.querySelector('.product-image-slider__main-video');
      if (existingVideo) {
        existingVideo.remove();
      }

      if (mediaType === 'video' && mediaInfo && mediaInfo.sources) {
        if (this.mainImage) {
          this.mainImage.style.display = 'none';
        }
        if (zoomIcon) {
          zoomIcon.style.display = 'none';
        }

        const videoContainer = document.createElement('div');
        videoContainer.className = 'product-image-slider__main-video';

        const videoEl = document.createElement('video');
        videoEl.controls = true;
        videoEl.preload = 'metadata';
        videoEl.playsInline = true;
        videoEl.setAttribute('playsinline', '');

        if (newSrc) {
          videoEl.poster = newSrc;
        }

        const sources = [...mediaInfo.sources].sort((a, b) => b.width - a.width);
        sources.forEach(source => {
          const sourceEl = document.createElement('source');
          sourceEl.src = source.url;
          sourceEl.type = source.mimeType;
          videoEl.appendChild(sourceEl);
        });

        videoContainer.appendChild(videoEl);
        this.mainMediaWrapper.appendChild(videoContainer);

      } else if (mediaType === 'external_video' && mediaInfo) {
        if (this.mainImage) {
          this.mainImage.style.display = 'none';
        }
        if (zoomIcon) {
          zoomIcon.style.display = 'none';
        }

        const videoContainer = document.createElement('div');
        videoContainer.className = 'product-image-slider__main-video';

        const iframe = document.createElement('iframe');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = 'none';

        if (mediaInfo.host === 'youtube') {
          iframe.src = `https://www.youtube.com/embed/${mediaInfo.externalId}?enablejsapi=1`;
        } else if (mediaInfo.host === 'vimeo') {
          iframe.src = `https://player.vimeo.com/video/${mediaInfo.externalId}`;
        }

        videoContainer.appendChild(iframe);
        this.mainMediaWrapper.appendChild(videoContainer);

      } else {
        if (zoomIcon) {
          zoomIcon.style.display = '';
        }

        if (!this.mainImage) {
          const img = document.createElement('img');
          img.className = 'product-image-slider__main-image';
          this.mainMediaWrapper.appendChild(img);
          this.mainImage = img;
        }

        this.mainImage.style.display = '';
        this.mainImage.removeAttribute('srcset');

        if (this._loadingTimer) {
          clearTimeout(this._loadingTimer);
          this._loadingTimer = null;
        }

        if (this._imageCache[newSrc] && this._imageCache[newSrc].complete && this._imageCache[newSrc].naturalWidth > 0) {
          this.mainImage.classList.remove('is-loading');
          this.mainImage.src = newSrc;
          this.mainImage.alt = newAlt;
          this.mainImage.classList.add('fade-in');
          setTimeout(() => {
            this.mainImage.classList.remove('fade-in');
          }, 300);
        } else {
          this._loadingTimer = setTimeout(() => {
            this.mainImage.classList.add('is-loading');
          }, 150);

          const img = this._imageCache[newSrc] || new Image();
          this._imageCache[newSrc] = img;
          img.onload = () => {
            if (this._loadingTimer) {
              clearTimeout(this._loadingTimer);
              this._loadingTimer = null;
            }
            this.mainImage.src = newSrc;
            this.mainImage.alt = newAlt;
            this.mainImage.classList.remove('is-loading');
            this.mainImage.classList.add('fade-in');
            setTimeout(() => {
              this.mainImage.classList.remove('fade-in');
            }, 300);
          };
          img.onerror = () => {
            if (this._loadingTimer) {
              clearTimeout(this._loadingTimer);
              this._loadingTimer = null;
            }
            this.mainImage.src = newSrc;
            this.mainImage.alt = newAlt;
            this.mainImage.classList.remove('is-loading');
          };
          if (!img.src) {
            img.src = newSrc;
          }
        }
      }
    }

    goToSlide(index) {
      if (index < 0 || index >= this.mediaCount) return;

      this.currentIndex = index;

      const thumbnail = this.thumbnails[index];
      if (!thumbnail) return;

      const mediaId = thumbnail.dataset.mediaId;

      this.renderMedia(mediaId, thumbnail);

      if (this.mainMediaWrapper) {
        this.mainMediaWrapper.dataset.currentMediaId = mediaId;
      }

      this.thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
      });

      this.scrollThumbnailIntoView(index);
      this.updateNavButtons();

      this.dispatchEvent(new CustomEvent('slider:change', {
        detail: { index, mediaId },
        bubbles: true
      }));
    }

    prevSlide() {
      if (this.currentIndex > 0) {
        this.goToSlide(this.currentIndex - 1);
      }
    }

    nextSlide() {
      if (this.currentIndex < this.mediaCount - 1) {
        this.goToSlide(this.currentIndex + 1);
      }
    }

    scrollThumbnails(direction) {
      if (!this.thumbnailsContainer) return;

      const scrollAmount = direction === 'prev'
        ? -this.thumbnailScrollAmount
        : this.thumbnailScrollAmount;

      this.thumbnailsContainer.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }

    scrollThumbnailIntoView(index) {
      const thumbnail = this.thumbnails[index];
      if (!thumbnail || !this.thumbnailsContainer) return;

      const containerRect = this.thumbnailsContainer.getBoundingClientRect();
      const thumbRect = thumbnail.getBoundingClientRect();

      if (thumbRect.left < containerRect.left) {
        this.thumbnailsContainer.scrollBy({
          left: thumbRect.left - containerRect.left - 10,
          behavior: 'smooth'
        });
      } else if (thumbRect.right > containerRect.right) {
        this.thumbnailsContainer.scrollBy({
          left: thumbRect.right - containerRect.right + 10,
          behavior: 'smooth'
        });
      }
    }

    preloadAllImages() {
      const srcsToLoad = [];
      this.thumbnails.forEach(thumb => {
        const type = thumb.dataset.mediaType || 'image';
        if (type !== 'image') return;
        const src = thumb.dataset.fullSrc;
        if (src && !this._imageCache[src]) {
          srcsToLoad.push(src);
        }
      });

      if (srcsToLoad.length === 0) return;

      const startLoading = () => {
        srcsToLoad.forEach((src, i) => {
          setTimeout(() => {
            if (this._imageCache[src]) return;
            const img = new Image();
            img.src = src;
            this._imageCache[src] = img;
          }, i * 100);
        });
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(startLoading);
      } else {
        setTimeout(startLoading, 200);
      }
    }

    updateNavButtons() {
      if (this.prevMainBtn) {
        this.prevMainBtn.disabled = this.currentIndex === 0;
      }
      if (this.nextMainBtn) {
        this.nextMainBtn.disabled = this.currentIndex === this.mediaCount - 1;
      }
    }

    updateThumbnailNavButtons() {
      if (!this.thumbnailsContainer) return;

      const { scrollLeft, scrollWidth, clientWidth } = this.thumbnailsContainer;
      const isAtStart = scrollLeft <= 0;
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      if (this.prevThumbBtn) {
        this.prevThumbBtn.disabled = isAtStart;
      }
      if (this.nextThumbBtn) {
        this.nextThumbBtn.disabled = isAtEnd;
      }
    }

    setActiveByMediaId(mediaId) {
      const thumbnailIndex = Array.from(this.thumbnails).findIndex(
        thumb => thumb.dataset.mediaId === String(mediaId)
      );

      if (thumbnailIndex !== -1) {
        this.goToSlide(thumbnailIndex);
      }
    }
  }

  customElements.define('product-image-slider', ProductImageSlider);
}

